/**
 * SmartIntentBar, Sprint 1.4. The single-input super-entry.
 *
 * The customer types one freeform line ("send Sarah flowers for her
 * birthday" / "browse hampers under $100" / "open Sarah's profile").
 * The smart-intent-classify edge function (Haiku 4.5) returns an
 * intent + extracted slots, and this component routes them.
 *
 * Pioneer alignment (ux-pioneers-wisdom.md):
 * - Norman: single obvious affordance (one input). Suggestion chips
 *   beneath signal what kinds of input work.
 * - Krug: don't make me think. Type whatever, system figures it out.
 *   Conventions: Enter to submit, ✕ to clear (standard).
 * - Wroblewski: smart defaults via pre-filled suggestion chips on
 *   click. Mobile-first single column. Submit button hidden when
 *   input is empty.
 * - Krishna: the chat-vs-browse mode-choice is collapsed into one
 *   input. The system disambiguates instead of the user.
 * - Stanley: aria-live region announces classification result;
 *   keyboard-only flow works (Tab → input → suggestions → Enter).
 *
 * Routing (pre-Phase 2 placeholder):
 *   - chat   → /order-demo with brief in query param (existing
 *              OrderConcierge guest flow as the temporary chat target)
 *   - browse → /team with the extracted slots as filters (placeholder
 *              for the Phase 2 browse picker)
 *   - nav    → direct route based on nav_target + recipient_name
 *   - ambiguous → render clarification chips inline; user picks one
 *
 * Every submission writes to `interaction_captures` from day 1 so
 * future agents can analyse what users actually type.
 */

import { useState, useRef, useEffect, KeyboardEvent, ReactNode } from "react";
import { useNavigate } from "react-router-dom";
// S7a, swapped ArrowRight → ArrowUp on the submit button to match
// the chat-style send affordance Heidi uses (Lucky's preference).
// MS11, Mic added for the voice-input stylism Lucky asked for in
// the hero. Wires to Web Speech API where supported (Chrome / Edge);
// shows a polite "not available" message on unsupported browsers.
// MS13, ArrowRight reintroduced for the lembra-catering-style
// suggestion-row label ("Try one of these →"). The full LucideIcon
// type is also imported so callers can pass per-chip icons via the
// new `landingChips` prop.
// MS58: ArrowRight removed from imports — the only consumer was the
// "Try one of these →" label, which Lucky read as misleading
// ("click to advance" not "label of the row below"). Pure text now.
// MS507: Mic removed per Lucky ("the issue still persists" — the
// hero mic was the only one left after MS505 hid the in-chat mic,
// and to the user that's still a "chat widget" mic). Web Speech API
// plumbing and lembra-mic-pulse keyframe deleted in the same pass.
import { ArrowUp, Loader2, X, type LucideIcon } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { captureInteraction } from "@/lib/captureInteraction";
// MS57 (REVERTED 2026-05-20 by Lucky): the alive-mascot ThinkingStrip
// used to render below the bar during the classifier parse step
// ("Reading your brief…" / "Making sense of it…"). Lucky flagged
// that this preloader sat OUTSIDE the chat box, and the chat-
// concierge experience should be that every loading affordance
// appears INSIDE the chat thread as part of the conversation.
// MS203 already moved the in-chat loading affordance into
// ThinkingBubble (inside InlineConcierge). Now the parse step's
// 1-2s classify latency is signalled by the Loader2 spinner inside
// the submit button (line ~803) — once the classifier returns and
// InlineConcierge mounts, ThinkingBubble takes over as the in-
// thread "thinking" affordance. No more loading state floating
// outside the chat box.
import { useReducedMotion } from "@/hooks/use-reduced-motion";

// MS507: Web Speech API type aliases + getSpeechRecognitionCtor()
// removed alongside the mic button.

interface ClassifyVerdict {
  intent: "chat" | "browse" | "nav" | "ambiguous";
  confidence: number;
  reasoning: string;
  recipient_name?: string;
  occasion?: string;
  vendor_category?: string;
  budget_aud?: number;
  nav_target?: string;
  clarifications?: string[];
}

interface SmartIntentBarProps {
  /** Optional context for classifier disambiguation. */
  recentRecipientNames?: string[];
  /** Override the default placeholder copy. */
  placeholder?: string;
  /** Override the default suggestion chips. */
  suggestions?: string[];
  /** MS59: when provided, non-ambiguous classifier verdicts are
   *  handed up to the parent instead of the bar's default navigate()
   *  routing. The parent gets (verdict, originalText) so it can
   *  seed an inline conversation surface with the exact brief the
   *  user typed (the SmartIntentBar's text is cleared on submit so
   *  the parent can't recover it any other way). Ambiguous verdicts
   *  always render clarification chips inline regardless — recovery
   *  flow stays self-contained. Default false (full routing). */
  onResultOverride?: (verdict: ClassifyVerdict, originalText: string) => void;
  className?: string;

  /** Sprint 2.5a A.0/B (landing rev), typewriter-cycled placeholder.
   *  When provided, the bar cycles through these phrases to signal
   *  "live & breathing" (Norman: signifier). Pauses on focus / hover
   *  so it never betrays a typing user. Falls back to `placeholder`
   *  if not provided. The actual aria-label stays static so screen
   *  readers don't get bombarded. */
  cyclingPlaceholders?: string[];

  /** Sprint 2.5a A.0/B (landing rev), when true, the chip row is
   *  pulled INSIDE the same rounded shell as the input, separated by
   *  a hairline divider. Chips render as inline editorial text with
   *  center-dot separators (Aesop catalogue navigation pattern), not
   *  as pills. Off by default so authed surfaces stay unchanged. */
  landingMode?: boolean;

  /** Sprint 2.5a A.0/B (landing rev), extra inline element rendered
   *  after the chips inside the chip row (typically a "browse →"
   *  link). Only rendered when `landingMode` is true. */
  landingTrailer?: ReactNode;

  /** MS13, when provided alongside `landingMode`, replaces the
   *  string-only `suggestions` row with a lembra-catering-style
   *  pill carousel: "Try one of these →" mono uppercase label,
   *  white-bg pill chips with a per-chip Lucide icon. On mobile
   *  the chips rotate one-at-a-time every ~4.5s (paused on hover
   *  / focus); desktop shows all chips. Falls back to plain
   *  string suggestions when not provided. */
  landingChips?: Array<{ label: string; icon: LucideIcon }>;

  /** Sprint 462 (2026-05-21): pre-populate the input on first render.
   *  Used by the chooser-to-celebrations hand-off — when a buyer
   *  clicks a footer category on lembra.com.au, that category's
   *  representative prompt is forwarded via `?q=…` and seeded here
   *  so the conversation has context instead of starting cold. */
  initialValue?: string;
}

const DEFAULT_PLACEHOLDER =
  "Tell us what you're after, try \"send Sarah a birthday gift\" or \"browse hampers\"…";

const DEFAULT_SUGGESTIONS = [
  "Send a birthday gift for someone on the team",
  "Browse hampers under $100",
  "Open Sarah's profile",
];

export function SmartIntentBar({
  recentRecipientNames,
  placeholder = DEFAULT_PLACEHOLDER,
  suggestions = DEFAULT_SUGGESTIONS,
  onResultOverride,
  className = "",
  cyclingPlaceholders,
  landingMode = false,
  landingTrailer,
  landingChips,
  initialValue,
}: SmartIntentBarProps) {
  const navigate = useNavigate();
  // MS58: ref is a textarea now (was HTMLInputElement). The bar
  // started as a single-line input, which Lucky flagged because
  // longer briefs ("Sarah, our head of design, vegan, no nuts,
  // turning 40 next Tuesday — wants something low-key") just
  // scrolled horizontally and felt like the bar wasn't really
  // listening. Swapping to a textarea + auto-grow lets the bar
  // expand vertically as the brief grows, same as the in-app
  // ComposerInput pattern.
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // MS177: respect prefers-reduced-motion for the JS-driven
  // typewriter (CSS-level reduced-motion rules don't reach
  // setTimeout-driven animation). When true, render the first
  // cycling phrase statically and skip the tick chain entirely.
  const reducedMotion = useReducedMotion();

  const [text, setText] = useState(initialValue ?? "");
  const [classifying, setClassifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingClarifications, setPendingClarifications] = useState<string[] | null>(null);

  // MS58: auto-grow the textarea up to ~5 lines, then start
  // scrolling internally. Same pattern as ComposerInput. The 140px
  // ceiling keeps the bar from eating the chip row below on long
  // briefs — past that point the textarea scrolls, the bar stays
  // anchored. Re-runs on every `text` change so growth is immediate
  // (no debounce needed, the work is just two DOM writes).
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 140)}px`;
  }, [text]);

  // MS507: voice input state (voiceState / recognitionRef + the
  // handleVoiceClick async handler + both useEffects that managed
  // mount-time SpeechRecognition detection and unmount cleanup)
  // all removed alongside the mic button. The bar now does typing
  // only — same pattern as the in-app chat composer post-MS505.

  // Sprint 2.5a A.0/B (landing rev), typewriter cycling placeholder.
  // The bar's only liveness signifier. Slow on purpose: 55ms/char
  // typing, 1800ms hold, 28ms/char erasing, 400ms gap. Aesop-quiet,
  // not nervous.
  //
  // 2026-05-16 rewrite — Lucky flagged that the typewriter was
  // glitching: every focus, every click around the page, every
  // keystroke restarted the phrase from char 0 mid-cycle. Causes
  // were the prior implementation listing `text`, `cyclePaused`, and
  // `cycleIdx` in the effect deps — every change tore down the
  // running timer chain and remounted the whole effect, which reset
  // charIdx + phase to their initial values. The user saw a flicker
  // back to an empty placeholder + a half-typed restart of the
  // current phrase.
  //
  // The fix: ONE self-perpetuating tick chain that mounts once on
  // cyclingPlaceholders identity, never tears down on transient
  // state. Pause is non-destructive — when pausedRef flips true the
  // tick keeps looping at a quiet 300ms beat doing nothing, so when
  // the user blurs the input or clears their text the cycle resumes
  // exactly where it left off (same phrase, same charIdx, same
  // phase). cycleIdx + charIdx + phase live as local vars in the
  // effect closure (advanced inline rather than via React state),
  // so transitions between phrases happen without any re-mount.
  // Initial typedPlaceholder is "" so the input doesn't flash the
  // full first phrase on cold paint before typing starts.
  const [typedPlaceholder, setTypedPlaceholder] = useState("");

  // Pause flag read inside the tick. Updated via a separate effect
  // so changes don't restart the typewriter — the running tick just
  // observes the new value on its next iteration.
  //
  // MS176: removed the `cyclePaused` state component (was driven by
  // focus + hover handlers). Typing into the box is now the only
  // override — once `text.length > 0` the user is taking over and
  // the placeholder freezes; `classifying` also freezes while the
  // submit is in-flight so the placeholder doesn't dance under a
  // spinner.
  const pausedRef = useRef(false);
  useEffect(() => {
    pausedRef.current = text.length > 0 || classifying;
  }, [text, classifying]);

  useEffect(() => {
    if (!cyclingPlaceholders || cyclingPlaceholders.length === 0) {
      setTypedPlaceholder("");
      return;
    }

    // MS177: prefers-reduced-motion → no cycle at all. Show the
    // first phrase fully typed and stop. The user still gets the
    // editorial signal (a concrete example query) without the
    // motion. Re-runs if the user toggles the OS preference live.
    if (reducedMotion) {
      setTypedPlaceholder(cyclingPlaceholders[0]);
      return;
    }

    let cancelled = false;
    let cycleIdx = 0;
    let charIdx = 0;
    type Phase = "typing" | "holding" | "erasing";
    let phase: Phase = "typing";
    let timer: ReturnType<typeof setTimeout> | undefined;

    const tick = () => {
      if (cancelled) return;
      // Paused: keep the tick alive on a quiet beat so the moment
      // the pause lifts we resume on the next iteration. Don't
      // touch typedPlaceholder — the input shows whatever was last
      // displayed, so blur after typing leaves it visually steady.
      if (pausedRef.current) {
        timer = setTimeout(tick, 300);
        return;
      }

      const phrase = cyclingPlaceholders[cycleIdx];

      if (phase === "typing") {
        if (charIdx < phrase.length) {
          charIdx += 1;
          setTypedPlaceholder(phrase.slice(0, charIdx));
          timer = setTimeout(tick, 55);
        } else {
          phase = "holding";
          timer = setTimeout(tick, 1800);
        }
      } else if (phase === "holding") {
        phase = "erasing";
        timer = setTimeout(tick, 0);
      } else if (phase === "erasing") {
        if (charIdx > 0) {
          charIdx -= 1;
          setTypedPlaceholder(phrase.slice(0, charIdx));
          timer = setTimeout(tick, 28);
        } else {
          // Advance to the next phrase in the local cycle var, then
          // reset phase. No setState here — phrase changes happen
          // entirely inside this effect's closure.
          cycleIdx = (cycleIdx + 1) % cyclingPlaceholders.length;
          phase = "typing";
          timer = setTimeout(tick, 400);
        }
      }
    };

    timer = setTimeout(tick, 0);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // Effect only re-runs if cyclingPlaceholders IDENTITY or the
    // reduced-motion preference changes — VariantB/Dashboard pass
    // module-level constants for cyclingPlaceholders so this means
    // "mount once, run forever" in the common case. If a caller
    // ever passes an inline literal that changes every render, the
    // typewriter would re-mount on every render; that's a caller
    // bug, not ours. The reducedMotion dep means a user toggling
    // the OS preference live causes us to either start cycling
    // (false → true → false during the session is rare but cheap).
  }, [cyclingPlaceholders, reducedMotion]);

  const callClassifier = async (input: string): Promise<ClassifyVerdict | null> => {
    setError(null);

    // Capture every submission, before we even know the verdict.
    void captureInteraction({
      surface: "smart_input",
      event_type: "submitted",
      payload: { text: input, length: input.length },
    });

    try {
      // MS199: silent retry on network-shape failures. Same rationale
      // as MS198 for chat-concierge — Supabase puts edge functions
      // to sleep after ~5 min idle; first call after sleep cold-
      // starts (Deno JIT + Haiku 4.5 round-trip ~1-3s combined) and
      // can fail on flaky mobile networks. smart-intent-classify is
      // hit on EVERY hero submission before chat-concierge, so a
      // cold-start failure here surfaces as the user's very first
      // interaction failing — worst possible place to drop the ball.
      //
      // No "first turn" gate (unlike chat-concierge — classifier
      // has no session continuity, every call is independent). One
      // retry on any network-shape error. Server-returned errors
      // pass through unchanged.
      const body = {
        text: input,
        context: {
          recentRecipientNames,
          surface_route: typeof window !== "undefined" ? window.location.pathname : undefined,
        },
      };

      let invokeResult = await supabase.functions.invoke(
        "smart-intent-classify",
        { body },
      );

      if (invokeResult.error && !invokeResult.data) {
        await new Promise((r) => setTimeout(r, 800));
        invokeResult = await supabase.functions.invoke(
          "smart-intent-classify",
          { body },
        );
        if (!invokeResult.error && invokeResult.data) {
          void captureInteraction({
            surface: "smart_input",
            event_type: "classifier_cold_start_recovered",
            payload: { text_len: input.length },
          });
        }
      }

      const { data, error: invokeError } = invokeResult;
      if (invokeError) throw new Error(invokeError.message);
      if (!data?.ok || !data?.verdict) {
        throw new Error(data?.error ?? "Classifier returned no verdict");
      }

      const verdict = data.verdict as ClassifyVerdict;
      // Capture the verdict alongside the input, gives us the full
      // round-trip for "what did the user mean?" analysis.
      void captureInteraction({
        surface: "smart_input",
        event_type: "classified",
        payload: {
          text: input,
          intent: verdict.intent,
          confidence: verdict.confidence,
          recipient_name: verdict.recipient_name,
          occasion: verdict.occasion,
          vendor_category: verdict.vendor_category,
          budget_aud: verdict.budget_aud,
          nav_target: verdict.nav_target,
        },
      });

      return verdict;
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError("We couldn't understand that just now. Try again, or pick a suggestion below.");
      void captureInteraction({
        surface: "smart_input",
        event_type: "classify_failed",
        payload: { text: input, error: msg },
      });
      return null;
    }
  };

  const routeVerdict = (verdict: ClassifyVerdict, originalText: string) => {
    setPendingClarifications(null);

    // MS59: ambiguous ALWAYS renders the clarification chips inline —
    // it's a recovery flow that needs to stay self-contained inside
    // the bar regardless of which surface is embedding it. The parent
    // never gets called for ambiguous; the user picks a clarification
    // chip which re-submits a more specific brief and we re-classify
    // from there. Doing this BEFORE the onResultOverride hop avoids
    // forcing every embedding caller to re-implement the clarification
    // UX.
    if (verdict.intent === "ambiguous") {
      setPendingClarifications(
        verdict.clarifications ?? [
          "Send a celebration gift",
          "Browse our catalogue",
          "Open someone's profile",
        ],
      );
      return;
    }

    // MS59: if the parent provides onResultOverride, hand the verdict
    // + original text up and let them decide what to do (typically:
    // inline the chat conversation into the same surface instead of
    // navigating). The parent typically swaps the SmartIntentBar
    // out for an inline surface, so passing originalText through
    // gives them the brief without depending on the bar's text
    // state (which is about to become irrelevant).
    // Sprint 1.4's pre-Phase 2 placeholder routing falls through only
    // when no override is wired — i.e. on surfaces that haven't been
    // inlined yet (Cmd-K, settings, etc.).
    if (onResultOverride) {
      onResultOverride(verdict, originalText);
      return;
    }

    // Build query params from extracted slots, destination pages can
    // pick these up to prefill (when destinations exist; pre-Phase 2
    // they're informational only).
    const params = new URLSearchParams();
    params.set("brief", originalText);
    if (verdict.recipient_name) params.set("recipient", verdict.recipient_name);
    if (verdict.occasion) params.set("occasion", verdict.occasion);
    if (verdict.vendor_category) params.set("category", verdict.vendor_category);
    if (verdict.budget_aud) params.set("budget", String(verdict.budget_aud));

    switch (verdict.intent) {
      case "chat":
        // Sprint 1.5: routes to the real chat widget at /chat with
        // brief + recipient + occasion in query params (the Chat page
        // reads them as initialBrief and seeds the conversation).
        navigate(`/chat?${params.toString()}`);
        break;

      case "browse":
        // Sprint 1.6: routes to the real browse picker with any
        // extracted slots (category, budget) as initial filters.
        // The Browse page reads `category`, `budget`, `recipient`,
        // `occasion` from query params and seeds the FilterRail.
        navigate(`/browse?${params.toString()}`);
        break;

      case "nav": {
        const target = verdict.nav_target;
        if (target === "settings") {
          navigate("/settings");
        } else if (target === "team") {
          navigate("/team");
        } else if (target === "dashboard") {
          navigate("/dashboard");
        } else if (target === "profile" && verdict.recipient_name) {
          // We'd ideally resolve the recipient_name → team_members.id
          // here, but that's an extra DB hit. Pre-Phase 2: route to
          // /team with the name in a query param so the user can land
          // close to their target.
          navigate(`/team?search=${encodeURIComponent(verdict.recipient_name)}`);
        } else {
          // Unknown nav_target, fall through to ambiguous handling
          setPendingClarifications(
            verdict.clarifications ?? [
              "Open the team page",
              "Open my settings",
              "Send a celebration gift",
            ],
          );
        }
        break;
      }
    }
  };

  const handleSubmit = async (raw?: string) => {
    const input = (raw ?? text).trim();
    if (!input || classifying) return;

    setClassifying(true);
    const verdict = await callClassifier(input);
    setClassifying(false);

    if (verdict) {
      routeVerdict(verdict, input);
    }
  };

  // MS58: Enter submits, Shift+Enter inserts a newline. Universal
  // chat convention (Slack/Linear/ChatGPT/Heidi/catering composer
  // all behave the same way). Was Enter-submits-no-modifier before,
  // which on a single-line <input> didn't matter — once we moved
  // to a textarea, the user can't insert a newline without it.
  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void handleSubmit();
    }
  };

  // Sprint 2.5a B.3 P2-6, `/` global shortcut to focus the smart
  // input. Linear / Notion / GitHub convention. Bails when the user
  // is already typing in another input/textarea/contentEditable so
  // we don't intercept legitimate `/` characters mid-typing. Also
  // bails on classifying state (input is disabled then).
  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key !== "/") return;
      if (classifying) return;
      const target = e.target as HTMLElement | null;
      if (target) {
        const tag = target.tagName.toLowerCase();
        if (tag === "input" || tag === "textarea" || target.isContentEditable) {
          return;
        }
      }
      // Don't intercept when modifier keys are held, those are
      // browser shortcuts (e.g. ⌘/ on Notion).
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      e.preventDefault();
      inputRef.current?.focus();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [classifying]);

  const handleSuggestionClick = (suggestion: string) => {
    setText(suggestion);
    inputRef.current?.focus();
    void handleSubmit(suggestion);
  };

  const handleClarificationClick = (clarification: string) => {
    setPendingClarifications(null);
    setText(clarification);
    inputRef.current?.focus();
    void handleSubmit(clarification);
  };

  const handleClear = () => {
    setText("");
    setError(null);
    setPendingClarifications(null);
    inputRef.current?.focus();
  };

  // Decide which placeholder string to actually show. When cycling
  // is active and the user hasn't typed anything, use the typewriter
  // string (which is empty for one tick at the very start, then
  // grows). Otherwise fall back to the static placeholder.
  const effectivePlaceholder =
    cyclingPlaceholders && cyclingPlaceholders.length > 0 && !text
      ? typedPlaceholder
      : placeholder;

  // Input row, shared between landing and default modes. In landing
  // mode the surrounding shell provides the border + radius, so this
  // stays unstyled at the container level.
  //
  // MS58: `items-center` → `items-end` so the mic/clear/send buttons
  // bottom-anchor against the textarea. When the textarea grows,
  // the buttons stay at the bottom edge (chat-composer convention)
  // rather than tracking the moving vertical center.
  const inputRow = (
    <div
      className={
        landingMode
          ? "relative flex items-end px-4 py-3.5"
          : "relative flex items-end rounded-2xl border-2 border-charcoal/15 bg-white px-4 py-3 shadow-sm focus-within:border-coral"
      }
    >
      <textarea
        ref={inputRef}
        rows={1}
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          if (error) setError(null);
        }}
        onKeyDown={handleKeyDown}
        // MS176: do NOT pause the typewriter on focus / blur. Lucky
        // flagged that clicking into the box killed the typing
        // animation before the user typed a single character. The
        // typewriter should only stop once the user actually OVERRIDES
        // it by typing — `text.length > 0` (read inside pausedRef
        // below) already covers that. Pre-MS176 the pause-on-focus
        // was defensive flicker-prevention from an old typewriter
        // implementation that no longer exists.
        placeholder={effectivePlaceholder}
        aria-label="What would you like to do?"
        disabled={classifying}
        maxLength={500}
        // resize-none kills the bottom-right drag handle — auto-grow
        // is doing the sizing, manual resize would fight it. The
        // leading-snug + py-1 give the single-line state the same
        // visual height the old <input> had (so the bar doesn't
        // suddenly look "taller" before any text is typed).
        //
        // MS193: min-h-[36px] matches the mic/send button row so the
        // empty/single-line state fills the row height. Without it,
        // the textarea was shorter than the buttons, items-end pushed
        // the textarea to the bottom, and the placeholder visually
        // dangled below the buttons' midline instead of sitting flush
        // at the top of the pill.
        className="flex-1 resize-none bg-transparent text-base leading-snug text-navy outline-none placeholder:text-charcoal/45 disabled:opacity-60 py-1 min-h-[36px] sm:min-h-[42px]"
        style={{ fontFamily: "DM Sans, sans-serif", maxHeight: 140 }}
      />
      {/* MS507: voice mic button removed. Typing-only — same as
          the in-app chat composer after MS505. */}
      {text && !classifying && (
        <button
          type="button"
          onClick={handleClear}
          aria-label="Clear input"
          className="mr-2 rounded-full p-1 text-charcoal/55 hover:bg-charcoal/8 hover:text-navy"
        >
          <X size={16} strokeWidth={2.5} />
        </button>
      )}
      <button
        type="button"
        onClick={() => void handleSubmit()}
        disabled={!text.trim() || classifying}
        aria-label="Submit"
        className="inline-flex items-center justify-center rounded-xl bg-coral p-2.5 text-white transition-opacity hover:bg-coral/90 disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-coral focus:ring-offset-2 sm:p-3"
      >
        {classifying ? (
          <Loader2
            className="h-4 w-4 animate-spin sm:h-[18px] sm:w-[18px]"
            strokeWidth={2.25}
          />
        ) : (
          <ArrowUp
            className="h-4 w-4 sm:h-[18px] sm:w-[18px]"
            strokeWidth={2.25}
          />
        )}
      </button>
      {/* MS507: lembraMicPulse keyframes removed with the mic. */}
    </div>
  );

  // Suggestion row. Three rendering paths:
  //   1. landingMode + landingChips → MS13 catering-style pill row
  //      (mono uppercase label + Lucide-icon pills, mobile carousel)
  //   2. landingMode without landingChips → original inline editorial
  //      row with center-dot separators (kept for back-compat)
  //   3. Default mode → unchanged pill row
  const showSuggestions = !text && !pendingClarifications;
  const suggestionRow = showSuggestions ? (
    landingMode ? (
      landingChips && landingChips.length > 0 ? (
        <LandingChipRow
          chips={landingChips}
          onChipClick={handleSuggestionClick}
          trailer={landingTrailer}
        />
      ) : (
      <div
        className="flex flex-wrap items-baseline gap-x-2 gap-y-1 border-t border-charcoal/8 px-4 py-3"
        style={{ fontFamily: "DM Sans, sans-serif" }}
      >
        <span className="text-xs uppercase tracking-[0.14em] text-charcoal/45">
          Try
        </span>
        {suggestions.map((s, i) => (
          <span key={s} className="flex items-baseline gap-x-2">
            {i > 0 && (
              <span aria-hidden="true" className="text-charcoal/30">·</span>
            )}
            <button
              type="button"
              onClick={() => handleSuggestionClick(s)}
              className="text-sm text-navy/80 underline decoration-transparent underline-offset-4 transition-colors hover:text-coral hover:decoration-coral focus:outline-none focus:ring-2 focus:ring-coral"
            >
              {s}
            </button>
          </span>
        ))}
        {landingTrailer && (
          <span className="flex items-baseline gap-x-2">
            <span aria-hidden="true" className="text-charcoal/30">·</span>
            {landingTrailer}
          </span>
        )}
      </div>
      )
    ) : (
      <div className="mt-3 flex flex-wrap items-center gap-1.5">
        <span
          className="text-xs text-charcoal/55"
          style={{ fontFamily: "DM Sans, sans-serif" }}
        >
          Try:
        </span>
        {suggestions.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => handleSuggestionClick(s)}
            className="rounded-full border border-navy/20 px-2.5 py-0.5 text-xs text-navy/80 hover:border-coral hover:text-coral focus:outline-none focus:ring-2 focus:ring-coral"
            style={{ fontFamily: "DM Sans, sans-serif" }}
          >
            {s}
          </button>
        ))}
      </div>
    )
  ) : null;

  // MS176: hover used to pause the typewriter too ("keeps the
  // placeholder still while the user is reading or about to click").
  // Removed — Lucky flagged that the typewriter dying any time the
  // pointer brushed the bar felt broken. Same principle as the
  // focus removal above: typing is the only override. Hovering or
  // clicking around should leave the cycle running.
  return (
    <div className={`w-full ${className}`}>
      {landingMode ? (
        // Single rounded shell, input + hairline divider + chip row
        // all live as one affordance. Aesop-quiet, one tactile object.
        <div className="overflow-hidden rounded-2xl border-2 border-charcoal/15 bg-white shadow-sm focus-within:border-coral transition-colors">
          {inputRow}
          {suggestionRow}
        </div>
      ) : (
        <>
          {inputRow}
        </>
      )}

      {/* Status + error region (aria-live for screen readers).
          MS57 REVERTED (2026-05-20): the alive-mascot ThinkingStrip
          used to render here during classification. Lucky's call:
          loading affordances belong INSIDE the chat box, not
          floating below the input. The submit button's Loader2
          spinner (line ~803) covers the 1-2s classify latency,
          then InlineConcierge mounts with its own in-thread
          ThinkingBubble. This region now only surfaces errors. */}
      <div role="status" aria-live="polite" className="min-h-[24px]">
        {error && (
          <p
            className="mt-2 text-xs text-coral"
            style={{ fontFamily: "DM Sans, sans-serif" }}
          >
            {error}
          </p>
        )}
      </div>

      {/* Clarification chips when intent was ambiguous, same in both
          modes since the surface treatment matters less than the
          visual urgency of the recovery moment. */}
      {pendingClarifications && pendingClarifications.length > 0 && (
        <div className="mt-3 space-y-2">
          <p
            className="text-xs font-semibold text-navy"
            style={{ fontFamily: "DM Sans, sans-serif" }}
          >
            Did you mean…
          </p>
          <div className="flex flex-wrap gap-2">
            {pendingClarifications.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => handleClarificationClick(c)}
                className="rounded-full border-2 border-coral bg-coral/5 px-3 py-1.5 text-sm text-coral hover:bg-coral hover:text-white focus:outline-none focus:ring-2 focus:ring-coral"
                style={{ fontFamily: "DM Sans, sans-serif" }}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Default-mode suggestion chips, landingMode rendered the
          chips inside the shell already, so skip when landingMode. */}
      {!landingMode && suggestionRow}
    </div>
  );
}

/* ─── LandingChipRow (MS13) ────────────────────────────────────────
   Lembra's adaptation of the lembra-catering.com CarouselChips
   pattern: a "Try one of these →" mono uppercase label above a row
   of pill chips. Each chip gets a Lucide icon (coral) on the left
   and the suggestion text. On mobile (<640px) only one chip is
   visible at a time and the row auto-rotates every 4.5s; desktop
   shows all chips at once. Hover or focus pauses the rotation so
   the user isn't dragged through moving targets while reading. */

const CHIP_ROTATE_MS = 4500;

function LandingChipRow({
  chips,
  onChipClick,
  trailer,
}: {
  chips: Array<{ label: string; icon: LucideIcon }>;
  onChipClick: (label: string) => void;
  trailer?: ReactNode;
}) {
  const [startIdx, setStartIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const [isMobileLayout, setIsMobileLayout] = useState(false);

  // Track viewport via matchMedia. Mobile = 1 chip visible at a
  // time; desktop = all chips visible. 640px matches Tailwind's
  // sm: breakpoint and the catering source's threshold.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(max-width: 639px)");
    const update = () => setIsMobileLayout(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  // Mobile-only carousel rotation. Desktop shows everything so
  // there's nothing to rotate.
  useEffect(() => {
    if (!isMobileLayout || paused || chips.length <= 1) return;
    const id = window.setInterval(() => {
      setStartIdx((i) => (i + 1) % chips.length);
    }, CHIP_ROTATE_MS);
    return () => window.clearInterval(id);
  }, [isMobileLayout, paused, chips.length]);

  const visible = isMobileLayout ? [chips[startIdx % chips.length]] : chips;

  return (
    <div
      className="border-t border-charcoal/8 px-4 py-3"
      style={{ fontFamily: "DM Sans, sans-serif" }}
    >
      {/* MS58: arrow removed (Lucky: it was misleading — read as
          "click to advance" rather than just labelling the row below).
          Plain mono-uppercase label now, which matches the other
          editorial micro-headings used across the landing. */}
      <p
        className="mb-2 text-[11px] uppercase tracking-[0.16em] text-charcoal/55"
        style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
      >
        Try one of these
      </p>
      <div
        className="flex flex-nowrap items-center gap-2 overflow-hidden"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onFocusCapture={() => setPaused(true)}
        onBlurCapture={() => setPaused(false)}
      >
        {visible.map((chip) => {
          const Icon = chip.icon;
          return (
            <button
              key={chip.label}
              type="button"
              onClick={() => onChipClick(chip.label)}
              className="lembra-landing-chip inline-flex min-w-0 shrink-0 items-center gap-1.5 rounded-full border bg-white/70 px-3.5 py-1.5 text-[13px] text-navy/75 transition-colors hover:border-coral hover:text-coral focus:outline-none focus:ring-2 focus:ring-coral"
              style={{ borderColor: "rgba(29,52,97,0.15)" }}
            >
              <Icon
                size={13}
                strokeWidth={2.2}
                className="shrink-0"
                style={{ color: "#C4564A" }}
              />
              <span className="truncate">{chip.label}</span>
            </button>
          );
        })}
        {trailer && <span className="ml-1 shrink-0">{trailer}</span>}
      </div>
    </div>
  );
}
